import { useDep } from '~/compositions/dependency-container'
import GoogleMapsService from '~/services/GoogleMapsService'
import { nextTick, onBeforeUnmount, ref, Ref } from '~/utils/nuxt3-migration'
import { geolocationToGoogleGeolocation } from '~/utils/geolocation'
import { Geolocation } from '~/models/common/types'
import { noop } from '~/utils/function'
import { CENTER_OF_GREECE, GREECE_BOUNDS } from '~/constants/map'
import { GoogleMapControlPosition } from '~/constants/google/map'
import { useI18n } from '~/compositions/i18n'
import { useGoogleMapControl } from '~/compositions/google/map/control'
import { GeolocationService } from '~/services/navigator/GeolocationService'
import { sleep } from '~/utils/time'

export function useGoogleMap() {
  const googleMapsService = useDep(GoogleMapsService)
  const listeners: google.maps.MapsEventListener[] = []
  const marker = ref<google.maps.marker.AdvancedMarkerElement | null>(null)
  const circle = ref<google.maps.Circle>()
  const map = ref<google.maps.Map | null>(null)
  const { createControl, addControlToMap } = useGoogleMapControl()
  const { t } = useI18n()
  const geolocationService = useDep(GeolocationService)

  async function createMap(
    mapTemplateRef: Ref<HTMLElement | null>,
    options: any
  ): Promise<void> {
    const center =
      options.center?.lat && options.center?.lon
        ? options.center
        : CENTER_OF_GREECE
    const zoom = options.zoom || 12
    const minZoom = options.minZoom || 7
    const gestureHandling = options.gestureHandling || 'cooperative'
    const restriction = options.restriction || {
      latLngBounds: GREECE_BOUNDS,
      strictBounds: false
    }

    if (!mapTemplateRef.value) {
      throw new Error('Attempting to create a map without a map template ref')
    }
    map.value = await googleMapsService.createGoogleMap(mapTemplateRef.value, {
      center: geolocationToGoogleGeolocation(center),
      zoom,
      gestureHandling,
      streetViewControl: false,
      mapTypeControl: false,
      fullscreenControl: false,
      restriction,
      minZoom,
      keyboardShortcuts: false
    })
  }

  async function addZoomChangeListener(onZoomChange: (zoom: number) => any) {
    if (!map.value) {
      return
    }

    listeners.push(
      await googleMapsService.addListener(map.value, 'zoom_changed', () => {
        if (!map.value) {
          return
        }
        const zoom = map.value.getZoom()
        if (zoom) {
          onZoomChange(zoom)
        }
      })
    )
  }

  async function createMarker(position: Geolocation, options: any) {
    const draggable = options.draggable || false
    const onMarkerPositionChange = options.onMarkerPositionChange || noop

    if (!map.value) {
      throw new Error('Attempting to create a marker on a non-existent map')
    }

    marker.value = await googleMapsService.addMarkerToGoogleMap({
      map: map.value,
      position:
        (position && geolocationToGoogleGeolocation(position)) ||
        map.value.getCenter()
    })
    if (draggable) {
      await addMapMarkerListeners(onMarkerPositionChange)
    }
  }

  function moveMarkerToMapCenter() {
    const center = map.value!.getCenter()
    marker.value!.position = center
    if (circle.value && center) {
      circle.value.setCenter(center)
    }
  }

  async function addMapMarkerListeners(onMarkerPositionChange: any = noop) {
    listeners.push(
      await googleMapsService.addListener(
        map.value!,
        'center_changed',
        async () => {
          if (!map.value || !marker.value) {
            return
          }

          // This sleep is required to force the marker to move to the center of the map
          // after the zoom_changed event is triggered
          // TODO: Find a better way to handle this
          await sleep(50)
          moveMarkerToMapCenter()
        }
      )
    )

    listeners.push(
      await googleMapsService.addListener(map.value!, 'dragend', () => {
        if (!map.value || !marker.value) {
          return
        }

        moveMarkerToMapCenter()

        const newMarkerPosition = marker.value.position
        if (newMarkerPosition) {
          onMarkerPositionChange({
            lat: newMarkerPosition.lat,
            lon: newMarkerPosition.lng
          })
        }
      })
    )

    listeners.push(
      await googleMapsService.addListener(map.value!, 'zoom_changed', () => {
        if (!map.value || !marker.value?.position) {
          return
        }

        const mapCenter = map.value.getCenter()
        const markerCenter = new google.maps.LatLng(marker.value.position)

        if (mapCenter && mapCenter.equals(markerCenter)) {
          return
        }

        moveMarkerToMapCenter()

        const newMarkerPosition = marker.value.position
        if (newMarkerPosition) {
          onMarkerPositionChange({
            lat: newMarkerPosition.lat,
            lon: newMarkerPosition.lng
          })
        }
      })
    )
  }

  async function removeEventListeners() {
    for (const listener of listeners) {
      if (!listener) {
        continue
      }

      await googleMapsService.removeListener(listener)
    }
  }

  onBeforeUnmount(async () => {
    await removeEventListeners()
  })

  function moveMarker(geolocation: Geolocation) {
    if (!map.value || !marker.value) {
      return
    }
    const googleGeo = geolocationToGoogleGeolocation(geolocation)
    map.value.setCenter(googleGeo)
    marker.value.position = googleGeo
  }

  function addGpsLocationControlToMap(
    position: GoogleMapControlPosition,
    onLocation: (geolocation: Geolocation) => void = noop
  ): void {
    if (!map.value) {
      return
    }
    const buttonWithText = createControl(t('current location').toString())
    buttonWithText.addEventListener('click', async () => {
      const gpsLocation = await geolocationService.getCurrentGpsLocationWithSnackbarOnError()
      if (gpsLocation) {
        onLocation(gpsLocation)
      }
    })
    addControlToMap(buttonWithText, map.value, position)
  }

  function setZoom(zoom: number) {
    if (!map.value) {
      return
    }
    // Using next tick here to avoid wrong map/marker position due to premature zooming
    nextTick(() => map.value?.setZoom(zoom))
  }

  async function addCircle({
    radius,
    circleOptions = { editable: false }
  }: {
    radius: number
    circleOptions: google.maps.CircleOptions
  }) {
    if (!map.value) {
      return
    }

    await googleMapsService.addCircleToGoogleMap(
      map.value,
      radius,
      circleOptions
    )
  }

  async function addCircleAroundMarker(
    radius: number,
    circleOptions = { editable: false },
    onCircleCenterChange: (center: Geolocation) => any = noop,
    onCircleRadiusChange: (radius: number) => any = noop
  ) {
    if (!map.value || !marker.value) {
      return
    }

    circle.value = await googleMapsService.addCircleToGoogleMap(
      map.value,
      radius,
      { ...circleOptions, center: marker.value.position }
    )

    const bounds = circle.value.getBounds()

    if (bounds) {
      map.value.fitBounds(bounds)
    }

    listeners.push(
      await googleMapsService.addListener(
        circle.value,
        'center_changed',
        () => {
          // This event is triggered multiple times consecutively when dragging the bound marker
          const center = circle.value!.getCenter()
          if (center) {
            if (marker.value) {
              marker.value.position = center
            }
            onCircleCenterChange({ lat: center.lat(), lon: center.lng() })
          }
        }
      )
    )

    listeners.push(
      await googleMapsService.addListener(circle.value, 'radius_changed', () =>
        onCircleRadiusChange(circle.value!.getRadius())
      )
    )
  }

  return {
    createMap,
    createMarker,
    addMapMarkerListeners,
    moveMarker,
    addGpsLocationControlToMap,
    setZoom,
    addZoomChangeListener,
    addCircleAroundMarker,
    addCircle
  }
}
